
import React, {useState, useRef, useMemo, useCallback, useEffect} from 'react';
import { useHistory } from 'react-router-dom';
import { validarSesion } from '../tokens/useToken';
import { sendRequest } from '../requests/useRequest';
import { useSweetAlert } from '../sweetAlerts/useSweetAlert';

const COMPANY_COLORS = [
    { bg: '#e7f2ff', border: '#3187d6', text: '#15578e' },
    { bg: '#fff0dd', border: '#ef983b', text: '#925414' },
    { bg: '#e3f7ed', border: '#25a87b', text: '#176c50' },
    { bg: '#ffe9ee', border: '#df6987', text: '#963c57' },
    { bg: '#e9edff', border: '#7181d8', text: '#4656a1' },
    { bg: '#e4f6f8', border: '#24a6b5', text: '#176b77' },
    { bg: '#f5eafa', border: '#b478ca', text: '#7e458e' },
    { bg: '#f7f3df', border: '#a69e45', text: '#686221' },
    { bg: '#eaf5e2', border: '#7bb044', text: '#496e23' },
    { bg: '#fff0e9', border: '#dc8055', text: '#98512f' }
];

const normalizeCompany = (value) => {
    return String(value || 'SIN EMPRESA')
        .trim()
        .replace(/\s+/g, ' ')
        .toUpperCase();
};


const getCompanyColor = (company) => {
    const companyName = normalizeCompany(company);

    let hash = 0;

    for (let index = 0; index < companyName.length; index++) {
        hash = (
            (hash * 31) + companyName.charCodeAt(index)
        ) >>> 0;
    }

    return COMPANY_COLORS[hash % COMPANY_COLORS.length];
};

const toIsoDate = (value) => {
    if (!value) return '';

    if (value instanceof Date) {
        return [
            value.getFullYear(),
            String(value.getMonth() + 1).padStart(2, '0'),
            String(value.getDate()).padStart(2, '0')
        ].join('-');
    }

    const dateText = String(value);

    if (/^\d{4}-\d{2}-\d{2}/.test(dateText)) {
        return dateText.substring(0, 10);
    }

    return '';
};


const getTodayDate = () => {
    return toIsoDate(new Date());
};


const isPastDate = (value) => {
    const normalizedDate = toIsoDate(value);

    return Boolean(normalizedDate) &&
        normalizedDate < getTodayDate();
};


const formatDate = (value) => {
    const formattedDate = toIsoDate(value);

    if (!formattedDate) {
        return 'No disponible';
    }

    const [year, month, day] = formattedDate.split('-');

    return `${day}/${month}/${year}`;
};


const formatLongDate = (value) => {
    const formattedDate = toIsoDate(value);

    if (!formattedDate) {
        return 'Fecha no disponible';
    }

    const [year, month, day] = formattedDate
        .split('-')
        .map(Number);

    const parsedDate = new Date(year, month - 1, day);

    const formattedText = parsedDate.toLocaleDateString(
        'es-MX',
        {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }
    );

    return formattedText.charAt(0).toUpperCase() +
        formattedText.slice(1);
};

const getAddress = (item) => {
    if (!item) {
        return 'Sin ubicación registrada';
    }

    return [
        item.address,
        item.colony || item.colony_name,
        item.cp
    ]
        .filter(Boolean)
        .join(', ') || 'Sin ubicación registrada';
};


const getEventData = (event) => {
    if (!event) return {};

    return {
        ...(event.extendedProps || {}),
        ...event
    };
};


export const useSanitaryAgenda = () => {
    //? SERVICES
    const history = useHistory();
    //?REFRENCES
    const calendarRef = useRef(null);
    const requestRef = useRef(0);
    const lastRangeRef = useRef('');

    //? CALENDARTE STATE
    const [events, setEvents] = useState([]);
    const [currentView, setCurrentView] = useState('dayGridMonth');
    const [calendarTitle, setCalendarTitle] = useState('');
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    //?SIDEBAR STATE
    const [sidebarVisible, setSidebarVisible] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [selectedDate, setSelectedDate] = useState('');
    const [panelMode, setPanelMode] = useState('empty');


    //? RESCHEDULING STATE
    const [pendingMove, setPendingMove] = useState(null);

     //? TOAST ALERT 
    const [showToast, setShowToast] = useState(false);
    const [toastType, setToastType] = useState("info");
    const [toastTitle, setToastTitle] = useState("");
    const [toastMessage, setToastMessage] = useState("");

    //? CUSTOM ALERT GLOBAL
    const [customAlert, setCustomAlert] = useState({
        visible: false,
        mode: "alert",
        type: "info",
        title: "",
        message: "",
        onAccept: null,
        onCancel: null
    });


     //#region ALERT GLOBAL 
    const showToastAlert = (
        type = "info",
        title = "",
        message = ""
    ) => {

        setToastType(type);
        setToastTitle(title);
        setToastMessage(message);
        setShowToast(true);

    };

    const closeToastAlert = () => {

        setShowToast(false);
        setToastTitle("");
        setToastMessage("");

    };
    //#endregion

  const showCustomAlert = (type, title, message) => {

        console.log("🚨 showCustomAlert ejecutado");

        setCustomAlert({
            visible: true,
            mode: "alert",
            type,
            title,
            message,
            onAccept: () => {
                setCustomAlert(prev => ({
                    ...prev,
                    visible: false
                }));
            },
            onCancel: null
        });

    };

   
    const showCustomAlertConfirm = ({
        type = "warning",
        title,
        message,
        buttonText = "Aceptar",
        cancelText = "Cancelar",
        variant = "default",
        rescheduleData = null
    }) => {

        return new Promise((resolve) => {

            setCustomAlert({
                visible: true,
                mode: "confirm",
                type,
                title,
                message,
                buttonText,
                cancelText,
                variant,
                rescheduleData,

                onAccept: () => {
                    setCustomAlert(prev => ({
                        ...prev,
                        visible: false
                    }));

                    resolve(true);
                },

                onCancel: () => {
                    setCustomAlert(prev => ({
                        ...prev,
                        visible: false
                    }));

                    resolve(false);
                }
            });

        });
    };


    //? FETCH SAMPLING QUOTATIONS
    const fetchQuotations = useCallback(async (dateInfo) => {

        const startDate = toIsoDate(dateInfo.startStr);
        const endDate = toIsoDate(dateInfo.endStr);
        const requestNumber = ++requestRef.current;
        setLoading(true);

        try {

            const response = await sendRequest({method: 'GET' }, `QuoterDeterminations/ListQuoter/${startDate}/${endDate}`);

            if (requestNumber !== requestRef.current) {
                return;
            }

            if (response.code === 200) {

                const records = Array.isArray(response.data)
                    ? response.data
                    : [];

                const mappedEvents = records
                    .map((item, index) => {

                        const companyColor = getCompanyColor(item.name_company);
                        return {
                            ...item,

                            id: String(
                                item.id != null
                                    ? item.id
                                    : `${item.code || 'event'}-${index}`
                            ),

                            title: String(
                                item.code || 'Sin código'
                            ),

                            start: toIsoDate(item.date_sampling),
                            allDay: true,
                            backgroundColor: companyColor.bg,
                            borderColor: companyColor.border,
                            textColor: companyColor.text,
                            classNames: ['sanitaryAgenda-event'],
                            extendedProps: {
                                ...item,
                                companyColor
                            }
                        };

                    })
                    .filter(item => Boolean(item.start));

                setEvents(mappedEvents);
                setSelectedEvent(previousEvent => {
                    if (!previousEvent) {
                        return null;
                    }

                    return mappedEvents.find(
                        item => item.id === previousEvent.id
                    ) || null;

                });

            } else if (response.code === 401) {
                validarSesion(history, response.code, fetchQuotations);
            } else {
                showToastAlert("error", "", response.code === 500 ? 'error' : 'warning', 'Agenda sanitaria', response.data?.msg || 'No fue posible consultar los muestreos.');
            }

        } catch (error) {
            if (requestNumber === requestRef.current) {
                showToastAlert("error", "Agenda sanitaria", "Ocurrió un error al consultar los muestreos.");
            }

        } finally {

            if (requestNumber === requestRef.current) {
                setLoading(false);
            }
        }
    }, [history]);

    //? FILTER EVENTS
    

    const filteredEvents = useMemo(() => {

        const searchText = search.trim().toLowerCase();

        if (!searchText) {
            return events;
        }

        return events.filter(item => {

            const eventData = getEventData(item);

            return [
                eventData.code,
                eventData.name_company,
                eventData.phone,
                eventData.address
            ].some(value => {

                return String(value || '')
                    .toLowerCase()
                    .includes(searchText);

            });

        });

    }, [events, search]);

    //? GROUP EVENTS BY DATE

    const eventsByDate = useMemo(() => {
        const groupedEvents = {};
        filteredEvents.forEach(item => {
            const eventDate = toIsoDate(item.start);

            if (!eventDate) {
                return;
            }

            if (!groupedEvents[eventDate]) {
                groupedEvents[eventDate] = [];
            }

            groupedEvents[eventDate].push(item);
        });

        return groupedEvents;
    }, [filteredEvents]);


    //? SELECTED DAY EVENTS
    const dayEvents = useMemo(() => {

        if (!selectedDate) {
            return [];
        }
        return eventsByDate[selectedDate] || [];
    }, [eventsByDate, selectedDate]);


    //? CALENDAR STATISTICS

    const statistics = useMemo(() => {
        const uniqueCompanies = new Set(
            filteredEvents.map(item =>
                normalizeCompany(item.name_company)
            )
        );

        return {
            total: filteredEvents.length,
            companies: uniqueCompanies.size
        };

    }, [filteredEvents]);


    //* CALENDAR NAVIGATION

    const changeView = (view) => {

        if (calendarRef.current) {
            calendarRef.current
                .getApi()
                .changeView(view);

        }

        setCurrentView(view);
    };


    const navigate = (action) => {

        if (!calendarRef.current) {
            return;
        }

        const calendarApi = calendarRef.current.getApi();
        switch (action) {
            case 'prev': calendarApi.prev();
                break;

            case 'next': calendarApi.next();
                break;

            case 'today': calendarApi.today();
                break;

            default:
                break;

        }
    };


    //? SIDEBAR ACTIONS

    const openDayList = (dateValue) => {
        setSelectedDate(toIsoDate(dateValue));
        setSelectedEvent(null);
        setPanelMode('list');
        setSidebarVisible(true);
    };


    const openDetails = (eventItem) => {
        if (!eventItem) {
            return;
        }

        setSelectedEvent(eventItem);
        setSelectedDate(toIsoDate(eventItem.start));
        setPanelMode('detail');
        setSidebarVisible(true);
    };


    const selectEvent = (eventInfo) => {
        const selectedId = String(eventInfo.event.id);
        const foundEvent = events.find(
            item => item.id === selectedId
        );

        if (foundEvent) {
            openDetails(foundEvent);
        }
    };


    const backToList = () => {
        setSelectedEvent(null);
        setPanelMode('list');
    };


    const closeSidebar = () => {
        setSelectedEvent(null);
        setSelectedDate('');
        setPanelMode('empty');
        setSidebarVisible(false);

    };

    //* EVENT COLORS

    const applyEventColor = (eventInfo) => {
        const eventData = eventInfo.event.extendedProps;

        const companyColor = eventData.companyColor ||
            getCompanyColor(eventData.name_company);

        eventInfo.el.style.setProperty('--sd-card-bg', companyColor.bg);
        eventInfo.el.style.setProperty('--sd-card-border', companyColor.border);
        eventInfo.el.style.setProperty('--sd-card-text', companyColor.text);
        eventInfo.el.style.backgroundColor = companyColor.bg;
        eventInfo.el.style.borderColor = companyColor.border;
    };

    //* CALENDAR EVENT CARD
    const renderEventCard = (eventInfo) => {
        const eventData = eventInfo.event.extendedProps;
        const companyColor = eventData.companyColor || getCompanyColor(eventData.name_company);

        return (
            <div className="sanitaryAgenda-card" style={{'--sd-card-bg': companyColor.bg, '--sd-card-border': companyColor.border, '--sd-card-text': companyColor.text}}
                title={`${eventInfo.event.title} - ${eventData.name_company || 'Sin empresa'}`}>
                <span className="sanitaryAgenda-card-dot" />

                <div className="sanitaryAgenda-card-content">
                    <strong>
                        {eventInfo.event.title}
                    </strong>

                    <span>
                        {eventData.name_company || 'Sin empresa registrada'}
                    </span>
                </div>

                <i className="fa fa-arrows sanitaryAgenda-card-drag" />
            </div>
        );
    };


    //? CALENDAR LIST CARD
    const renderListCard = (eventInfo) => {
        const eventData = eventInfo.event.extendedProps;
        const companyColor = eventData.companyColor ||
            getCompanyColor(eventData.name_company);

        return (
            <div className="sanitaryAgenda-list-card" style={{'--sd-card-bg': companyColor.bg, '--sd-card-border': companyColor.border, '--sd-card-text': companyColor.text}}>
                <div className="sanitaryAgenda-list-icon">
                    <i className="fa fa-flask" />
                </div>

                <div className="sanitaryAgenda-list-info">
                    <strong>
                        {eventInfo.event.title}
                    </strong>

                    <span>
                        {eventData.name_company || 'Sin empresa'}
                    </span>
                </div>

                <span className="sanitaryAgenda-list-view" style={{cursor: 'pointer'}}>
                    Ver detalles
                    <i className="fa fa-angle-right" />
                </span>
            </div>
        );
    };

    const updateSamplingDate = async (quotationId, samplingDate) => {

        const payload = {
            id_quoter: quotationId,
            date_sample: `${samplingDate}T00:00:00`
        };

        const response = await sendRequest(
            {
                method: 'PUT',
                body: JSON.stringify(payload)
            },
            'QuoterDeterminations/UpdateSamplingDate'
        );

        if (response.code !== 200) {

            if (response.code === 401) {
                validarSesion(history, response.code);
            }

            throw new Error(
                response.data?.msg ||
                response.data?.message ||
                "No fue posible actualizar la fecha de muestreo."
            );
        }

        return response;
    };

    const handleEventDrop = async (eventInfo) => {

        const originalEvent = events.find(item => item.id === String(eventInfo.event.id));

        if (!originalEvent) {
            eventInfo.revert();
            return;
        }

        const previousDate = toIsoDate(originalEvent.start);
        const nextDate = toIsoDate(eventInfo.event.start);

        if (isPastDate(nextDate)) {
            eventInfo.revert();
            showToastAlert("warning", "Fecha no válida", "Solo puedes reprogramar para hoy o una fecha futura.");
            return;
        }

        if (previousDate === nextDate) {
            return;
        }

        const confirmed = await showCustomAlertConfirm({
            type: "warning",
            title: "Reprogramar muestreo",
            buttonText: "Confirmar cambio",
            cancelText: "Cancelar",
            variant: "reschedule",
            rescheduleData: {
                code: originalEvent.code,
                previousDate: formatDate(previousDate),
                nextDate: formatDate(nextDate)
            }
        });

        if (!confirmed) {
            eventInfo.revert();
            return;
        }

        try {
            const quotationId = originalEvent.id_quoter ?? originalEvent.extendedProps?.id_quoter;

            if (!quotationId) {
                showToastAlert("error", "", "No se encontró el identificador de la cotización.");
            }

            await updateSamplingDate(quotationId, nextDate);

            setEvents(previousEvents =>
                previousEvents.map(item =>
                    item.id === originalEvent.id
                        ? {
                            ...item,
                            start: nextDate,
                            date_sampling: nextDate,
                            extendedProps: {
                                ...item.extendedProps,
                                date_sampling: nextDate
                            }
                        }
                        : item
                )
            );

            setSelectedEvent(previousEvent => {
                if (!previousEvent || previousEvent.id !== originalEvent.id) {
                    return previousEvent;
                }

                return {
                    ...previousEvent,
                    start: nextDate,
                    date_sampling: nextDate,
                    extendedProps: {
                        ...previousEvent.extendedProps,
                        date_sampling: nextDate
                    }
                };
            });
            showToastAlert("success", "Muestreo reprogramado", `La fecha de muestreo se actualizó al ${formatDate(nextDate)} correctamente.`);
        } catch (error) {

            eventInfo.revert();

            showToastAlert(
                "error",
                "Error al reprogramar",
                error.message ||
                "No fue posible guardar la nueva fecha de muestreo."
            );
        }
    };



    const startSampling = () => {

        if (!selectedEvent) {
            return;
        }

        const eventData = getEventData(selectedEvent);

        const quotationCode = eventData?.code || selectedEvent.code || selectedEvent.extendedProps?.code;

        if (!quotationCode) {
            showToastAlert("warning", "Código no disponible", "No se encontró el código de la cotización.");
            return;
        }

        history.push(`/sanitaria/muestreo/${encodeURIComponent(quotationCode)}`);

    };


    useEffect(() => {
        const updateCalendarSize = () => {
            if (calendarRef.current) {
                calendarRef.current
                    .getApi()
                    .updateSize();

            }

        };

        window.addEventListener('resize',updateCalendarSize);
        return () => {
            window.removeEventListener('resize', updateCalendarSize);
        };
    }, []);


   
    const views = [
        {
            id: 'dayGridMonth',
            text: 'Mes',
            icon: 'fa-calendar'
        },
        // {
        //     id: 'timeGridWeek',
        //     text: 'Semana',
        //     icon: 'fa-columns'
        // },
        // {
        //     id: 'timeGridDay',
        //     text: 'Día',
        //     icon: 'fa-clock-o'
        // },
        {
            id: 'listMonth',
            text: 'Lista',
            icon: 'fa-list'
        }
    ];

    

    return {

        //? References
        calendarRef,
        lastRangeRef,

        //? Calendar state
        events,
        currentView,
        calendarTitle,
        search,
        loading,

        //? Sidebar state
        sidebarVisible,
        selectedEvent,
        selectedDate,
        panelMode,


        //? Derived data
        filteredEvents,
        dayEvents,
        statistics,
        views,

        //? Calendar setters
        setSearch,
        setCalendarTitle,
        setCurrentView,
        fetchQuotations,

        //? Calendar actions
        changeView,
        navigate,
        selectEvent,
        applyEventColor,
        renderEventCard,
        renderListCard,

        //? Sidebar actions
        openDayList,
        openDetails,
        backToList,
        closeSidebar,

        //? Rescheduling actions
        handleEventDrop,

        //? Navigation actions
        startSampling,

        //? Date helpers
        formatDate,
        formatLongDate,
        isPastDate,

        //? Event helpers
        getEventData,
        getAddress,

        
        //? CUSTOM ALERT
        customAlert,

        //? TOAST ALERT
        showToast,
        toastType,
        toastTitle,
        toastMessage,
        closeToastAlert,
    };

};
